import {
  transformFormToTwapParams,
  TwapOrderFormData,
} from '@/features/order/chat/lib/twap-transform'
import { parseFormattedNumber } from '@/features/order/lib/number-format-util'
import {
  buildExitBracketLegs,
  ExitBracketConfig,
} from '@/features/order/order-form/lib/exit-bracket'
import { resolveBaseCurrency } from '@/features/order/order-form/lib/instrument-resolution'
import { AlgoServiceCommandList } from '@/types/generated/algo'
import { Denomination, OrderSide, OrderType, TimeInForce } from '@/types/generated/common/trading'
import type { Exchange, Instrument } from '@/types/generated/common/types'
import {
  LimitPriceAnchor,
  LimitPriceOffsetUnit,
  OrderExecutionCommand,
  OrderExecutionCommandList,
} from '@/types/generated/execution'
import { PresetPayload } from '@/types/generated/preset'

/** One order row as a preset leg, bound to the account that row submits under. */
export interface FormItemRaw {
  symbol: string
  side: 'buy' | 'sell'
  amount: number
  amountUnit?: string
  baseCurrency?: string
  itemType: 'instrument' | 'option'
  contractSize?: string
  accountId: string
  exchange: Exchange
  tradeVenue: number
}

export interface ManualPresetOptions {
  items: FormItemRaw[]
  price: string
  priceAnchor?: 'price' | 'mid' | 'best_bid' | 'best_ask'
  priceOffsetUnit?: 'quote_value' | 'bps' | 'percent' | 'ticks'
  isMarketOrder: boolean
  timeInForce?: TimeInForce
  reduceOnly?: boolean
  postOnly?: boolean
  /**
   * Exit Condition (TP/SL) bracket to attach as extra reduce-only legs.
   * Only applies when there's exactly one item — mirrors the single-instrument
   * restriction on the live submit path (submitLimitMarketOrder).
   */
  exitCondition?: ExitBracketConfig
}

export interface TwapPresetOptions {
  items: FormItemRaw[]
  twapIntervalMinutes: number
  twapFrequencySeconds: number
  twapFrequencyMinSeconds: number
  twapFrequencyMaxSeconds: number
  twapFrequencyMode: 'fixed' | 'random'
  twapOrderSize: number
  twapOrderSizeMin: number
  twapOrderSizeMax: number
  twapOrderSizeMode: 'fixed' | 'random'
}

export interface LimitChasePresetOptions {
  items: FormItemRaw[]
}

function resolveContractSize(item: FormItemRaw): number {
  if (item.itemType !== 'option') return 1
  return Math.max(1, Number(item.contractSize ?? '1') || 1)
}

function isBaseAmount(item: FormItemRaw, instrumentMap: Map<string, Instrument>): boolean {
  const baseCurrency = resolveBaseCurrency(instrumentMap, item.symbol, {
    baseCurrency: item.baseCurrency,
  } as Instrument)
  return item.itemType === 'option' || item.amountUnit === baseCurrency
}

export function buildManualPresetPayload(
  opts: ManualPresetOptions,
  instrumentMap: Map<string, Instrument> = new Map()
): PresetPayload {
  const anchor = opts.priceAnchor ?? 'price'
  const isRelativeLimit = !opts.isMarketOrder && anchor !== 'price'

  const anchorMap: Record<string, LimitPriceAnchor> = {
    mid: LimitPriceAnchor.LIMIT_PRICE_ANCHOR_MID_PRICE,
    best_bid: LimitPriceAnchor.LIMIT_PRICE_ANCHOR_BEST_BID,
    best_ask: LimitPriceAnchor.LIMIT_PRICE_ANCHOR_BEST_ASK,
  }
  const unitMap: Record<string, LimitPriceOffsetUnit> = {
    quote_value: LimitPriceOffsetUnit.LIMIT_PRICE_OFFSET_UNIT_QUOTE_VALUE,
    bps: LimitPriceOffsetUnit.LIMIT_PRICE_OFFSET_UNIT_BPS,
    percent: LimitPriceOffsetUnit.LIMIT_PRICE_OFFSET_UNIT_PERCENT,
    ticks: LimitPriceOffsetUnit.LIMIT_PRICE_OFFSET_UNIT_TICKS,
  }

  const commands: OrderExecutionCommand[] = opts.items.map((item) => {
    const effectiveAmount = item.amount * resolveContractSize(item)
    const denomination = isBaseAmount(item, instrumentMap)
      ? Denomination.DENOMINATION_BASE
      : Denomination.DENOMINATION_QUOTE

    return {
      accountId: item.accountId,
      new: {
        accountId: item.accountId,
        orderSide: item.side === 'buy' ? OrderSide.ORDER_SIDE_BUY : OrderSide.ORDER_SIDE_SELL,
        symbol: item.symbol,
        quantity: effectiveAmount.toString(),
        orderType: opts.isMarketOrder ? OrderType.ORDER_TYPE_MARKET : OrderType.ORDER_TYPE_LIMIT,
        price:
          opts.isMarketOrder || isRelativeLimit
            ? undefined
            : parseFormattedNumber(opts.price).toString(),
        relativePrice: isRelativeLimit
          ? {
              anchor: anchorMap[anchor] ?? LimitPriceAnchor.LIMIT_PRICE_ANCHOR_MID_PRICE,
              offset: parseFormattedNumber(opts.price).toString(),
              offsetUnit:
                unitMap[opts.priceOffsetUnit ?? 'quote_value'] ??
                LimitPriceOffsetUnit.LIMIT_PRICE_OFFSET_UNIT_QUOTE_VALUE,
            }
          : undefined,
        denomination,
        reduceOnly: opts.reduceOnly || undefined,
        postOnly: opts.isMarketOrder ? undefined : opts.postOnly || undefined,
        tif: opts.isMarketOrder ? undefined : opts.timeInForce,
      },
    }
  })

  // Exit-bracket legs are absolute-price and pure (no live-market lookups), so
  // they can be precomputed here exactly like the live submit path does — see
  // buildExitBracketLegs. One entry order only: a single TP/SL pair can't apply
  // across a multi-symbol basket.
  if (opts.items.length === 1 && opts.exitCondition) {
    const entry = commands[0].new
    const entrySide = opts.items[0].side
    if (entry) {
      const { accountId } = opts.items[0]
      const legs = buildExitBracketLegs(entry, entrySide, opts.exitCondition)
      commands.push(...legs.map((leg) => ({ accountId, new: { ...leg, accountId } })))
    }
  }

  return {
    commands: { commands } satisfies OrderExecutionCommandList,
    algoCommands: undefined,
  }
}

export function buildTwapPresetPayload(opts: TwapPresetOptions): PresetPayload {
  const commands = opts.items.map((item) => {
    const effectiveAmount = item.amount * resolveContractSize(item)
    const amountUnit = item.amountUnit ?? ''

    const formData: TwapOrderFormData = {
      symbol: item.symbol,
      quantity: effectiveAmount.toString(),
      side: item.side,
      unit: amountUnit,
    }

    if (opts.twapFrequencyMode === 'random') {
      formData.frequencyRandom = {
        min: opts.twapFrequencyMinSeconds * 1000,
        max: opts.twapFrequencyMaxSeconds * 1000,
      }
    } else {
      formData.frequencyFixed = opts.twapFrequencySeconds * 1000
    }

    if (opts.twapOrderSizeMode === 'random') {
      formData.orderSizeRandom = {
        min: opts.twapOrderSizeMin.toString(),
        max: opts.twapOrderSizeMax.toString(),
      }
    } else {
      formData.orderSizeFixed = opts.twapOrderSize.toString()
    }

    const params = transformFormToTwapParams(formData, item.accountId)
    return { startAlgo: { accountId: item.accountId, twap: params } }
  })

  return {
    commands: undefined,
    algoCommands: { commands } satisfies AlgoServiceCommandList,
  }
}

export function buildLimitChasePresetPayload(
  opts: LimitChasePresetOptions,
  instrumentMap: Map<string, Instrument> = new Map()
): PresetPayload {
  const commands = opts.items.map((item) => {
    const effectiveAmount = item.amount * resolveContractSize(item)
    const denomination = isBaseAmount(item, instrumentMap)
      ? Denomination.DENOMINATION_BASE
      : Denomination.DENOMINATION_QUOTE

    return {
      startAlgo: {
        accountId: item.accountId,
        limitChase: {
          symbol: item.symbol,
          quantity: effectiveAmount.toString(),
          orderSide: item.side === 'buy' ? OrderSide.ORDER_SIDE_BUY : OrderSide.ORDER_SIDE_SELL,
          accountId: item.accountId,
          exchange: item.exchange,
          denomination,
          tradeVenue: item.tradeVenue,
          // Advanced-TWAP per-slice knob; absent keeps standalone limit-chase behaviour.
          catchup: undefined,
        },
      },
    }
  })

  return {
    commands: undefined,
    algoCommands: { commands } satisfies AlgoServiceCommandList,
  }
}
